import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { playersService, playerTeamAssignmentsService } from '../../../services/supabaseService';
import LeagueImportPlayersModal from './LeagueImportPlayersModal';

const PlayerAssignmentPanel = ({ team, onClose, onRefresh }) => {
  const [activeAssignments, setActiveAssignments] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);
  const [historyAssignments, setHistoryAssignments] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [addForm, setAddForm] = useState({
    playerId: '',
    effectiveFrom: new Date()?.toISOString()?.split('T')?.[0],
    effectiveUntil: '',
    transferReason: '',
    season: ''
  });
  const [addError, setAddError] = useState('');
  const [addSubmitting, setAddSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [team?.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [assignments, players] = await Promise.all([
        playerTeamAssignmentsService?.getActiveByTeam(team?.id),
        playersService?.getAll()
      ]);
      setActiveAssignments(assignments || []);
      setAllPlayers(players || []);
    } catch (error) {
      console.error('Error fetching assignment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const data = await playerTeamAssignmentsService?.getByTeam(team?.id);
      setHistoryAssignments(data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleShowHistory = () => {
    setShowHistory(true);
    fetchHistory();
  };

  const handleAssignPlayer = async (e) => {
    e?.preventDefault();
    setAddError('');

    if (!addForm?.playerId) {
      setAddError('Please select a player');
      return;
    }

    setAddSubmitting(true);
    try {
      await playerTeamAssignmentsService?.assign({
        playerId: addForm?.playerId,
        teamId: team?.id,
        effectiveFrom: addForm?.effectiveFrom,
        effectiveUntil: addForm?.effectiveUntil || null,
        transferReason: addForm?.transferReason || null,
        season: addForm?.season || null
      });
      await fetchData();
      setShowAddPlayer(false);
      setAddForm({
        playerId: '',
        effectiveFrom: new Date()?.toISOString()?.split('T')?.[0],
        effectiveUntil: '',
        transferReason: '',
        season: ''
      });
      onRefresh?.();
    } catch (err) {
      setAddError(err?.message || 'Failed to assign player');
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleDeactivate = async (assignmentId) => {
    if (!confirm('Remove this player from the team? This will close their current assignment.')) return;
    try {
      await playerTeamAssignmentsService?.deactivate(assignmentId);
      await fetchData();
      onRefresh?.();
    } catch (error) {
      console.error('Error deactivating assignment:', error);
      setToastMsg('Failed to remove player from team');
      setTimeout(() => setToastMsg(null), 3000);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Batter': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Bowler': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'All-rounder': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'Wicket Keeper': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getReasonLabel = (reason) => {
    const labels = { auction: 'Auction', trade: 'Trade', replacement: 'Replacement', loan: 'Loan', free_transfer: 'Free Transfer', other: 'Other' };
    return labels?.[reason] || reason || '—';
  };

  // Players not already actively assigned to this team
  const assignedPlayerIds = new Set(activeAssignments?.map(a => a?.playerId));
  const availablePlayers = allPlayers?.filter(p => !assignedPlayerIds?.has(p?.id));
  const filteredAvailable = availablePlayers?.filter(p =>
    p?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[1200]">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-foreground">League Roster</h2>
                <span className="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
                  {team?.leagueName || 'League'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{team?.name} — {activeAssignments?.length} active players</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="History"
                iconPosition="left"
                onClick={handleShowHistory}>
                History
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Upload"
                iconPosition="left"
                onClick={() => setShowImport(true)}>
                Import CSV
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="UserPlus"
                iconPosition="left"
                onClick={() => setShowAddPlayer(true)}>
                Add Player
              </Button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-muted rounded-lg transition-colors">
                <Icon name="X" className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Active Roster */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Loading roster...</p>
            </div>
          ) : activeAssignments?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Users" className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground mb-4">No players assigned to this team yet</p>
              <Button variant="default" iconName="UserPlus" iconPosition="left" onClick={() => setShowAddPlayer(true)}>
                Add First Player
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {activeAssignments?.map((assignment) => (
                <div key={assignment?.id} className="p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-foreground">{assignment?.player?.name}</h3>
                        {assignment?.player?.jerseyNumber && (
                          <span className="text-xs text-muted-foreground">#{assignment?.player?.jerseyNumber}</span>
                        )}
                      </div>
                      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadgeColor(assignment?.player?.role)}`}>
                        {assignment?.player?.role}
                      </span>
                      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Icon name="Calendar" className="w-3 h-3" />
                          <span>Since {assignment?.effectiveFrom}</span>
                        </div>
                        {assignment?.season && (
                          <div className="flex items-center gap-1">
                            <Icon name="Trophy" className="w-3 h-3" />
                            <span>{assignment?.season}</span>
                          </div>
                        )}
                        {assignment?.transferReason && (
                          <div className="flex items-center gap-1">
                            <Icon name="ArrowRightLeft" className="w-3 h-3" />
                            <span>{getReasonLabel(assignment?.transferReason)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeactivate(assignment?.id)}
                      className="p-2 hover:bg-destructive/10 rounded-lg transition-colors ml-2"
                      title="Remove from team">
                      <Icon name="UserMinus" className="w-4 h-4 text-destructive" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6">
            <Button variant="outline" onClick={onClose} className="w-full">Close</Button>
          </div>
        </div>
      </div>

      {showImport && (
        <LeagueImportPlayersModal
          isOpen={showImport}
          onClose={() => setShowImport(false)}
          leagueTeam={team}
          onRefresh={() => { fetchData(); onRefresh?.(); }}
        />
      )}

      {/* Add Player Modal */}
      {showAddPlayer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[1400]">
          <div className="bg-card rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-foreground">Add Player to Roster</h3>
                <button onClick={() => setShowAddPlayer(false)} className="p-2 hover:bg-muted rounded-lg transition-colors">
                  <Icon name="X" className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              {addError && (
                <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm text-destructive">{addError}</p>
                </div>
              )}

              <form onSubmit={handleAssignPlayer} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Search Player</label>
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    placeholder="Search by name..."
                    iconName="Search"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Select Player <span className="text-destructive">*</span>
                  </label>
                  <select
                    value={addForm?.playerId}
                    onChange={(e) => setAddForm({ ...addForm, playerId: e?.target?.value })}
                    className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    size={Math.min(6, filteredAvailable?.length + 1)}>
                    <option value="">-- Select a player --</option>
                    {filteredAvailable?.map(player => (
                      <option key={player?.id} value={player?.id}>
                        {player?.name} ({player?.role}) {player?.teamName ? `— ${player?.teamName}` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {filteredAvailable?.length} players available from all countries
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Effective From</label>
                  <Input
                    type="date"
                    value={addForm?.effectiveFrom}
                    onChange={(e) => setAddForm({ ...addForm, effectiveFrom: e?.target?.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Season (Optional)</label>
                  <Input
                    type="text"
                    value={addForm?.season}
                    onChange={(e) => setAddForm({ ...addForm, season: e?.target?.value })}
                    placeholder="e.g. IPL 2025"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Reason (Optional)</label>
                  <select
                    value={addForm?.transferReason}
                    onChange={(e) => setAddForm({ ...addForm, transferReason: e?.target?.value })}
                    className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="">Select reason</option>
                    <option value="auction">Auction</option>
                    <option value="trade">Trade</option>
                    <option value="replacement">Replacement</option>
                    <option value="loan">Loan</option>
                    <option value="free_transfer">Free Transfer</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowAddPlayer(false)} disabled={addSubmitting} className="flex-1">Cancel</Button>
                  <Button type="submit" variant="default" disabled={addSubmitting} className="flex-1">
                    {addSubmitting ? 'Adding...' : 'Add to Roster'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[1400]">
          <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-foreground">Transfer History — {team?.name}</h3>
                <button onClick={() => setShowHistory(false)} className="p-2 hover:bg-muted rounded-lg transition-colors">
                  <Icon name="X" className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              {historyLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-sm text-muted-foreground">Loading history...</p>
                </div>
              ) : historyAssignments?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No assignment history found</p>
              ) : (
                <div className="space-y-2">
                  {historyAssignments?.map((assignment) => (
                    <div key={assignment?.id} className={`p-3 border rounded-lg ${assignment?.isActive ? 'border-primary/30 bg-primary/5' : 'border-border'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${assignment?.isActive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                          <div>
                            <p className="font-medium text-foreground text-sm">{assignment?.player?.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {assignment?.effectiveFrom} → {assignment?.effectiveUntil || 'Present'}
                              {assignment?.season ? ` · ${assignment?.season}` : ''}
                              {assignment?.transferReason ? ` · ${getReasonLabel(assignment?.transferReason)}` : ''}
                            </p>
                          </div>
                        </div>
                        {assignment?.isActive && (
                          <span className="text-xs font-medium text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded-full">Active</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4">
                <Button variant="outline" onClick={() => setShowHistory(false)} className="w-full">Close</Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {toastMsg && (
        <div className="fixed bottom-4 right-4 z-[9999] bg-destructive text-destructive-foreground px-4 py-3 rounded-lg shadow-lg text-sm font-medium">
          {toastMsg}
        </div>
      )}
    </div>
  );
};

export default PlayerAssignmentPanel;
